<template>
  <header
    class="bg-surface-default-low border-static-default-low fixed left-0 top-0 z-10 w-full items-center border-b px-32 py-18 transition-all duration-200 ease-in-out"
    :class="[
      isVisible ? 'translate-y-0 opacity-100 ' : '-translate-y-full opacity-0',
    ]"
  >
    <div class="divide-static-default-low flex items-center gap-8 divide-x-1">
      <Logo
        class="flex h-24 shrink-0 items-center"
        :disabled="!allowRedirection"
      />

      <slot />
    </div>
  </header>

  <div
    class="shrink-0 transition-[height] duration-200 ease-in-out"
    :class="[isVisible ? 'h-60' : 'h-0']"
  />
</template>

<script setup lang="ts">
import { onUnmounted, ref, watch } from 'vue'

import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'

import Logo from './components/Logo/Logo.vue'

const props = defineProps<{
  allowRedirection?: boolean
  scrollableContainer?: HTMLElement | null
}>()

const isVisible = ref(true)

let lastScrollY = 0
let accumulatedDelta = 0
const threshold = 150

const breakpoint = useClientOnlyComputedBreakpoint()

const handleScroll = () => {
  if (breakpoint.value > Breakpoint.SM) return

  const currentScrollY = props.scrollableContainer?.scrollTop ?? 0
  const delta = currentScrollY - lastScrollY

  accumulatedDelta += delta

  if (accumulatedDelta > threshold && isVisible.value) {
    isVisible.value = false
    accumulatedDelta = 0
  } else if (accumulatedDelta < -threshold && !isVisible.value) {
    isVisible.value = true
    accumulatedDelta = 0
  }

  if (
    (delta > 0 && accumulatedDelta < 0) ||
    (delta < 0 && accumulatedDelta > 0)
  ) {
    accumulatedDelta = delta
  }

  lastScrollY = currentScrollY
}

watch(
  () => props.scrollableContainer,
  (scrollableContainer) => {
    if (scrollableContainer && scrollableContainer instanceof HTMLElement) {
      scrollableContainer.addEventListener('scroll', handleScroll)
    }
  },
  { immediate: true },
)

watch(breakpoint, (newBreakpoint) => {
  if (newBreakpoint > Breakpoint.SM) {
    isVisible.value = true
  }
})

onUnmounted(() => {
  if (props.scrollableContainer) {
    props.scrollableContainer.removeEventListener('scroll', handleScroll)
  }
})
</script>
